import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

type SignInFnBody = {
  access_token?: string;
  refresh_token?: string;
  error?: string;
};

async function parseFnError(fnError: { message?: string; context?: { json: () => Promise<unknown> } }): Promise<string> {
  let msg = fnError.message?.trim() || 'Sign in failed';
  if (fnError.context?.json) {
    try {
      const body = (await fnError.context.json()) as SignInFnBody;
      if (body?.error) msg = body.error;
    } catch {
      /* keep msg */
    }
  }
  return msg;
}

async function signInWithUsername(username: string, password: string): Promise<void> {
  const { data, error: fnError } = await supabase.functions.invoke<SignInFnBody>('admin-sign-in', {
    body: { username: username.trim(), password },
  });

  if (fnError) {
    const msg = await parseFnError(fnError as Parameters<typeof parseFnError>[0]);
    if (msg === 'Edge Function returned a non-2xx status code' || msg.includes('Failed to send')) {
      throw new Error(
        'Username sign-in service is not available. Deploy the admin-sign-in Edge Function to Supabase (supabase functions deploy admin-sign-in --no-verify-jwt).',
      );
    }
    throw new Error(msg);
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  const access_token = data?.access_token;
  const refresh_token = data?.refresh_token;
  if (!access_token || !refresh_token) {
    throw new Error('Sign in failed — no session returned');
  }

  const { error: sessionErr } = await supabase.auth.setSession({ access_token, refresh_token });
  if (sessionErr) throw sessionErr;
}

export function LoginScreen() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const trimmed = username.trim();
      if (!trimmed) throw new Error('Enter your username');
      await signInWithUsername(trimmed, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign in failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative min-h-screen bg-canvas flex items-center justify-center px-6 py-10 overflow-hidden">
      {/* ambient violet bloom behind the card */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.14), transparent 60%)' }}
      />
      <div className="relative w-full max-w-sm">
        <div className="mb-8 flex flex-col items-center">
          <img src="/shado-wordmark.svg" alt="Shado" className="h-[52px] w-auto" />
          <p className="text-mutedDim text-[13px] uppercase tracking-[0.22em] mt-3">Admin</p>
        </div>
        <form onSubmit={onSubmit} className="admin-card space-y-4">
          <div>
            <label htmlFor="username" className="admin-label">
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              required
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="admin-input admin-input-lg"
              placeholder="yourname"
            />
          </div>
          <div>
            <label htmlFor="password" className="admin-label">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="admin-input admin-input-lg"
            />
          </div>
          {error ? <p className="admin-banner-error">{error}</p> : null}
          <button
            type="submit"
            disabled={loading}
            className="admin-btn admin-btn-primary w-full h-10 text-[14px]"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}
